import {AgentMode} from 'llm/chat-library/sidepanel/ChatTypeUtil';

export enum PromptSuggestionCategory {
  FIND_DATA = 'Find historical data',
  WRITE_REPORTS = 'Write reports',
  DESIGN_EXPERIMENTS = 'Design experiment',
  CAPTURE_DATA = 'Capture data',
  PREDICT_STRUCTURES = 'Predict structure',
}

/**
 * A PromptSuggestion is a fully-formed suggestion to kick off a new chat.
 *
 * PromptSuggestion should be JSON-serializable to support a future world
 * where suggestions can be generated on the backend by an LLM.
 *
 * The actual execution of the suggestion is handled by {@link runPromptSuggestion}.
 */
export interface PromptSuggestion {
  /** a unique identifier for the suggestion, currently used for analytics */
  id: string;
  /** a short user-facing summary of the suggestion */
  summary: string;
  /**
   * The action to take when the suggestion is clicked
   *
   * note: `prompt` is user-facing as it will be entered as the first message in the chat.
   */
  action:
    | {
        // Placeholder prompt suggestions that are not implemented. Should only be shown to logged out users
        // when LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT is enabled.
        type: 'NEW_CHAT_PLACEHOLDER';
        agentMode: 'PREDICT'; // This can be unioned with more placeholder types.
        prompt: string;
        input: {type: 'none'};
      }
    | {
        type: 'NEW_CHAT';
        agentMode: AgentMode.DEEP_RESEARCH | AgentMode.DATA_CAPTURE;
        prompt: string; // Ideally this is just MessageBlocks[]
        input:
          | {type: 'demo-file'; filename: string; name: string}
          | {
              // For now, benchling-object is only used for rendering. The object's name should be interpolated into the prompt.
              type: 'benchling-object';
              id: string;
            }
          | {type: 'none'};
      }
    | {
        type: 'NEW_CHAT';
        agentMode: AgentMode.ASK;
        prompt: string; // Ideally this is just MessageBlocks[]
        input:
          | {
              // For now, benchling-object is only used for rendering. The object's name should be interpolated into the prompt.
              type: 'benchling-object';
              id: string;
            }
          | {type: 'none'};
      }
    | {
        // Model Hub is not accessible via chat yet, so we can trigger a UI action to open the tab.
        type: 'MODEL_HUB_OPEN_3D_STRUCTURE';
        input: {type: 'benchling-object'; id: string};
      };
}
